import { useEffect, useRef, useState } from "react";
import { CockpitHeader } from "./components/cockpit/CockpitHeader";
import { DealsStrip } from "./components/cockpit/DealsStrip";
import { TasksRail } from "./components/cockpit/TasksRail";
import { WatchtowerActivityPanel } from "./components/cockpit/WatchtowerActivityPanel";
import { ChatBar } from "./components/cockpit/ChatBar";
import { UndoToastStack } from "./components/shared/UndoToastStack";
import { ChatExchange, type ChatTurn } from "./components/chat/ChatExchange";
import { FocusView } from "./components/focus/FocusView";
import { CrmTaskCreateDialog } from "./components/shared/CrmTaskCreateDialog";
import { DealUpdateConfirmDialog } from "./components/shared/DealUpdateConfirmDialog";
import { SaveNoteDialog } from "./components/shared/SaveNoteDialog";
import { ClosedLoanReviewModal } from "./components/focus/ClosedLoanReviewModal";
import { NewIntakeModal } from "./components/intake/NewIntakeModal";
import { PipelineModal } from "./components/pipeline/PipelineModal";
import { TasksModal } from "./components/tasks/TasksModal";
import { CalendarModal } from "./components/calendar/CalendarModal";
import { EmailWorkspaceModal } from "./components/email/EmailWorkspaceModal";
import { CockpitEmailPanel } from "./components/email/CockpitEmailPanel";
import { useCockpitData } from "./hooks/useCockpitData";
import { usePersonalTasks } from "./hooks/usePersonalTasks";
import { useTodoistProjects } from "./hooks/useTodoistProjects";
import { useCompletedTasks } from "./hooks/useCompletedTasks";
import { useCrmTaskActions } from "./hooks/useCrmTaskActions";
import {
  postChat,
  ApiError,
  createDealNote,
  updateDeal,
  type ChatDirective,
} from "./api";
import { parseHashRoute, toHash } from "./hashRoute";
import type { FocusTab } from "./components/focus/tabs";

type PendingTaskCreate = Extract<
  ChatDirective,
  { type: "propose_crm_task_create" }
>;
type PendingDealUpdate = Extract<
  ChatDirective,
  { type: "propose_deal_update" }
>;
type PendingNoteCreate = Extract<
  ChatDirective,
  { type: "propose_note_create" }
>;
type PendingClosedLoan = Extract<
  ChatDirective,
  { type: "propose_closed_loan_update" }
>;

export default function App() {
  const {
    deals,
    crmTasks,
    activity,
    activityNoiseSuppressed,
    alerts,
    pendingDismissals,
    restoredNotes,
    watchtowerLoading,
    loadState,
    error,
    dismissError,
    clearDismissError,
    reload,
    refreshWatchtower,
    dismissAlert,
    undoDismiss,
  } = useCockpitData();
  const personal = usePersonalTasks();
  const todoistProjects = useTodoistProjects();
  const completedPersonal = useCompletedTasks();
  const crmActions = useCrmTaskActions(reload);
  const [focusedDealId, setFocusedDealId] = useState<string | null>(null);
  // QW-1: the active focus tab lives here (lifted from FocusView) so the URL
  // has a single writer. Every fresh deal-open resets it to "overview" via
  // openDeal(); the restore-from-URL path is the one exception.
  const [focusedTab, setFocusedTab] = useState<FocusTab>("overview");
  // The hash exactly as the page loaded — captured before any effect can
  // rewrite it, so the one-time restore reads the user's real refresh URL.
  const initialHashRef = useRef(
    typeof window !== "undefined" ? window.location.hash : "",
  );
  // Guards the restore (run once) and gates the URL writer so it can't clobber
  // the URL before we've had a chance to read it.
  const restoredRef = useRef(false);
  const [chatTurn, setChatTurn] = useState<ChatTurn | null>(null);

  // Single entry point for opening a deal in focus mode. Resets the tab to
  // overview unless a specific tab is requested (only the URL restore does).
  const openDeal = (id: string, tab: FocusTab = "overview") => {
    setFocusedDealId(id);
    setFocusedTab(tab);
  };

  // QW-1 restore: once the deal list has loaded, parse the load-time hash and
  // re-open that deal+tab. Runs exactly once. Edge cases all fall to home:
  //  (1) deal id not in my loaded deals (deleted / no access) → skip (home),
  //  (2) unknown/unbuilt tab → parseHashRoute already normalized to overview,
  //  (3) no/garbage hash → parseHashRoute returns null → skip (home).
  useEffect(() => {
    if (restoredRef.current) return;
    // Wait for the cockpit load to settle so `deals` is authoritative. "error"
    // (deals empty) also unblocks → we simply find no match and stay home.
    if (loadState === "idle" || loadState === "loading") return;
    restoredRef.current = true;
    const route = parseHashRoute(initialHashRef.current);
    if (!route) return;
    if (!deals.some((d) => d.id === route.dealId)) return; // gone / no access
    openDeal(route.dealId, route.tab);
  }, [loadState, deals]);

  // QW-1 writer: mirror focus state into the URL via replaceState (no history
  // spam, so Back doesn't walk through every tab switch). Gated on restoredRef
  // so it never wipes the hash before the restore effect has read it. A bad
  // hash from the load (e.g. an inaccessible deal) self-heals to clean home
  // here once we've decided to stay home.
  useEffect(() => {
    if (!restoredRef.current) return;
    const next = toHash(focusedDealId, focusedTab);
    if (next === window.location.hash) return;
    const base = window.location.pathname + window.location.search;
    window.history.replaceState(null, "", next ? base + next : base);
  }, [focusedDealId, focusedTab]);
  // Per-tab session id for the chat brain's conversation memory. New UUID
  // on mount; tab refresh = fresh session (no sessionStorage).
  const [sessionId] = useState(() => crypto.randomUUID());
  // Chat-driven dialog state — directives from the brain open these.
  const [pendingTaskCreate, setPendingTaskCreate] =
    useState<PendingTaskCreate | null>(null);
  const [pendingDealUpdate, setPendingDealUpdate] =
    useState<PendingDealUpdate | null>(null);
  const [pendingNoteCreate, setPendingNoteCreate] =
    useState<PendingNoteCreate | null>(null);
  const [pendingClosedLoan, setPendingClosedLoan] =
    useState<PendingClosedLoan | null>(null);
  const [pipelineOpen, setPipelineOpen] = useState(false);
  const [tasksOpen, setTasksOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // Single place that routes any chat directive to UI state. Shared with FocusView.
  const handleChatDirective = (
    directive: ChatDirective | null | undefined,
  ) => {
    if (!directive) return;
    if (directive.type === "navigate") {
      openDeal(directive.deal_id);
    } else if (directive.type === "propose_crm_task_create") {
      setPendingTaskCreate(directive);
    } else if (directive.type === "propose_deal_update") {
      setPendingDealUpdate(directive);
    } else if (directive.type === "propose_note_create") {
      setPendingNoteCreate(directive);
    } else if (directive.type === "propose_closed_loan_update") {
      setPendingClosedLoan(directive);
    } else if (directive.type === "open_pipeline") {
      setPipelineOpen(true);
    } else if (directive.type === "open_tasks") {
      setTasksOpen(true);
    } else if (directive.type === "open_calendar") {
      setCalendarOpen(true);
    }
  };

  const submitChat = async (message: string) => {
    const id = crypto.randomUUID();
    setChatTurn({ id, question: message, response: undefined });
    try {
      const response = await postChat({
        message,
        session_id: sessionId,
        context: { view: "cockpit" },
      });
      setChatTurn({ id, question: message, response });
      handleChatDirective(response.directive);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? `${err.code} (HTTP ${err.status})`
          : err instanceof Error
            ? err.message
            : "unknown error";
      setChatTurn({ id, question: message, response: null, error: msg });
    }
  };

  // Toasts: warning toasts (⚠ — info worth acting on, e.g. "borrower not
  // linked") persist until dismissed; success/info auto-clear at 6s. All are
  // click-to-dismiss (see render).
  useEffect(() => {
    if (!toast || toast.includes("⚠")) return;
    const t = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  return (
    <>
      {focusedDealId ? (
        <FocusView
          dealId={focusedDealId}
          activeTab={focusedTab}
          onSelectTab={setFocusedTab}
          crmTasks={crmTasks}
          personal={personal}
          completedPersonal={completedPersonal}
          crmActions={crmActions}
          deals={deals}
          alerts={alerts}
          watchtowerLoading={watchtowerLoading}
          activity={activity}
          sessionId={sessionId}
          onBack={() => setFocusedDealId(null)}
          onChatDirective={handleChatDirective}
          onNewIntake={() => setIntakeOpen(true)}
          onOpenPipeline={() => setPipelineOpen(true)}
          onOpenTasks={() => setTasksOpen(true)}
          onOpenCalendar={() => setCalendarOpen(true)}
          onOpenEmail={() => setEmailOpen(true)}
          onDismissAlert={dismissAlert}
        />
      ) : (
        <>
          <div className="scan-beam" />
          <div className="corner-readout tl">SYS.NET // SECURE TUNNEL // ENCRYPTED</div>
          <div className="corner-readout br">
            EDGE.OS // © WESTEDGE FUNDING INC // CA DRE 02382109
          </div>

          <div className="cockpit">
            <CockpitHeader
              systemOnline={loadState !== "error"}
              onNewIntake={() => setIntakeOpen(true)}
              onOpenPipeline={() => setPipelineOpen(true)}
              onOpenTasks={() => setTasksOpen(true)}
              onOpenCalendar={() => setCalendarOpen(true)}
              onOpenEmail={() => setEmailOpen(true)}
            />
            <DealsStrip deals={deals} onSelect={(id) => openDeal(id)} />
            <TasksRail
              crmTasks={crmTasks}
              personalTasks={personal.tasks}
              onCompletePersonal={personal.complete}
              onSnoozePersonal={personal.snooze}
              onCreatePersonal={personal.create}
              onUpdatePersonal={personal.update}
              onReschedulePersonal={personal.reschedule}
              completedPersonal={completedPersonal}
              personalVersion={personal.version}
              projects={todoistProjects.projects}
              onCreateProject={todoistProjects.create}
              onCompleteCrm={crmActions.complete}
              onRescheduleCrm={crmActions.reschedule}
              onEditCrm={crmActions.edit}
            />
            <div className="center-stage">
              <div className="email-center-host">
                <CockpitEmailPanel />
              </div>
              {chatTurn && (
                <ChatExchange turn={chatTurn} onDismiss={() => setChatTurn(null)} />
              )}
              <ChatBar onSubmit={submitChat} />
            </div>
            <WatchtowerActivityPanel
              alerts={alerts}
              watchtowerLoading={watchtowerLoading}
              restoredNotes={restoredNotes}
              onRefreshWatchtower={refreshWatchtower}
              onFeedback={(id, kind, note) => dismissAlert(id, kind, note)}
              deals={deals}
              onCreateCrmTask={crmActions.create}
              onAddToMyList={personal.create}
              onSaveNote={async ({ deal_id, content }) => {
                await createDealNote(deal_id, { content });
              }}
              activity={activity}
              activityNoiseSuppressed={activityNoiseSuppressed}
            />
          </div>

          {loadState === "error" && (
            <div className="load-error">⚠ EDGE API · {error ?? "fetch failed"}</div>
          )}
        </>
      )}

      {/* Chat-driven write dialogs — overlay either view */}
      {pendingTaskCreate && (
        <CrmTaskCreateDialog
          initial={pendingTaskCreate.initial}
          deals={deals}
          onCreate={async (input) => {
            await crmActions.create(input);
            setPendingTaskCreate(null);
            setToast("✓ Created in Zoho");
          }}
          onCancel={() => setPendingTaskCreate(null)}
        />
      )}
      {pendingDealUpdate && (
        <DealUpdateConfirmDialog
          dealName={pendingDealUpdate.deal_name}
          changes={pendingDealUpdate.changes}
          onConfirm={async () => {
            await updateDeal(pendingDealUpdate.deal_id, pendingDealUpdate.fields);
            reload();
            setPendingDealUpdate(null);
            setToast(`✓ Updated ${pendingDealUpdate.deal_name}`);
          }}
          onCancel={() => setPendingDealUpdate(null)}
        />
      )}
      {pendingNoteCreate && (
        <SaveNoteDialog
          initial={pendingNoteCreate.initial}
          deals={deals}
          onSave={async ({ deal_id, content }) => {
            await createDealNote(deal_id, { content });
            setPendingNoteCreate(null);
            setToast("✓ Note saved");
          }}
          onCancel={() => setPendingNoteCreate(null)}
        />
      )}
      {pendingClosedLoan && (
        <ClosedLoanReviewModal
          directive={pendingClosedLoan}
          onSaved={(dealName) => {
            reload();
            setPendingClosedLoan(null);
            setToast(`✓ Saved closing details for ${dealName}`);
          }}
          onCancel={() => setPendingClosedLoan(null)}
        />
      )}
      {intakeOpen && (
        <NewIntakeModal
          onClose={() => setIntakeOpen(false)}
          onCreated={(dealId) => {
            setIntakeOpen(false);
            reload();
            openDeal(dealId);
          }}
          onToast={(msg) => setToast(msg)}
        />
      )}
      {pipelineOpen && (
        <PipelineModal
          deals={deals}
          onSelectDeal={(id) => {
            openDeal(id);
            setPipelineOpen(false);
          }}
          onClose={() => setPipelineOpen(false)}
        />
      )}
      {calendarOpen && (
        <CalendarModal
          deals={deals}
          onClose={() => setCalendarOpen(false)}
        />
      )}
      {emailOpen && <EmailWorkspaceModal onClose={() => setEmailOpen(false)} />}
      {tasksOpen && (
        <TasksModal
          crmTasks={crmTasks}
          personalTasks={personal.tasks}
          onSelectDeal={(id) => {
            openDeal(id);
            setTasksOpen(false);
          }}
          onCompleteCrm={crmActions.complete}
          onRescheduleCrm={crmActions.reschedule}
          onEditCrm={crmActions.edit}
          onCompletePersonal={personal.complete}
          onReschedulePersonal={personal.reschedule}
          onUpdatePersonal={personal.update}
          onClose={() => setTasksOpen(false)}
        />
      )}
      {toast && (
        <div
          className="edge-toast edge-toast-dismissable"
          onClick={() => setToast(null)}
          title="Click to dismiss"
        >
          {toast}
        </div>
      )}
      {dismissError && (
        <div className="edge-toast edge-toast-error" onClick={clearDismissError}>
          {dismissError}
        </div>
      )}
      <UndoToastStack pending={pendingDismissals} onUndo={undoDismiss} />
    </>
  );
}
